import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { AuthProvider, useAuth, UserManager } from 'oidc-react';
import { SIGNUP_USER_MANAGER_CONFIG, USER_MANAGER_CONFIG } from './config';
import { LoginComplete } from './App/LoginComplete';
import styled, { createGlobalStyle } from 'styled-components';
import { Interview } from './App/Interview';
import { TabItem, Tabs } from './Components/Tabs';
import { SYM } from './Components/MaterialSymbol';
import { PurposePreface } from './App/Interview/PurposePreface';
import { ApplicationStateProvider, getEasyLayout, Route, useRouteContext } from '@resistdesign/voltra/app/utils';
import { Login } from './App/Login';
import { Documents } from './App/Documents';
import { Profile } from './App/Profile';
import { Admin } from './App/Admin';
import { AudioRecordingScreen } from './App/Interview/AudioRecordingScreen';
import { InterviewComplete } from './App/Interview/InterviewComplete';
import { Theme } from './App/Theme';
import { EmployerCheckout } from './App/EmployerCheckout';
import { EmployerPaymentComplete } from './App/EmployerPaymentComplete';
// @ts-ignore
import Logo from 'url:./Assets/header-logo.svg';
// @ts-ignore
import LogoDarkBlue from 'url:./Assets/header-logo-dark-blue.svg';
import { AdminInfo, getAdminInfo } from '../common/utils/AdminUtils';
import { SIGN_UP_CONFIG } from '../common/SignUp';
import {
  clearStoredSignUpFlowState,
  getIsSigningUpAfterLogout,
  getSignUpFlowStateFromSearch,
  getSignUpOAuthState,
  getStoredSignUpFlowState,
  OAuthState,
  SignUpFlowState,
  storeSignUpFlowStateForLogout,
} from './Utils/SignUpFlowUtils';
import { useEmployerAccountStatus } from './Utils/useEmployerAccountStatus';

let RELOADED_SINCE_SIGN_UP_ATTEMPT = true;
let REDIRECTED_TO_SIGN_UP = false;

const {
  ROUTES: { DOCUMENTS: SIGN_UP_DOCUMENTS_PATH, EMPLOYER_CHECKOUT, EMPLOYER_PAYMENT_COMPLETE },
} = SIGN_UP_CONFIG;
const { post_logout_redirect_uri: SIGNOUT_URL } = USER_MANAGER_CONFIG;
const USER_MANAGER = new UserManager(USER_MANAGER_CONFIG as any);
const SIGNUP_USER_MANAGER = new UserManager(SIGNUP_USER_MANAGER_CONFIG as any);
const HEADER_TAB_DATA: {
  label: string;
  path: string;
  adminOnly?: boolean;
}[] = [
  { label: 'Interview', path: '/interview' },
  { label: 'Profile', path: '/profile' },
  { label: 'Admin', path: '/admin', adminOnly: true },
];
const GROUP_ADMIN_RESTRICTED_PATHS = ['/interview', '/profile'] as const;

const getPathIsGroupAdminRestricted = (path: string): boolean =>
  GROUP_ADMIN_RESTRICTED_PATHS.some((restrictedPath) => path.startsWith(restrictedPath));

const GlobalStyle = createGlobalStyle`
  html,
  body {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    width: 100vw;
    height: 100vh;
    margin: 0;
    padding: 0;
    overflow: hidden;
  }
`;
const LogoImg = styled.div.attrs({ className: 'logo-img' })`
  &.logo-img {
    background: url("${Logo}") no-repeat center;
    background-size: contain;
    aspect-ratio: 3.4;
    height: 100%;
    max-height: 100%;
    overflow: hidden;

    @media (prefers-color-scheme: light) {
      background: url("${LogoDarkBlue}") no-repeat center;
    }
  }
`;
const LayoutBase = styled.div`
  width: 100vw;
  height: 100vh;
  padding: 1em;
  gap: 1em;
`;
const {
  layout: Layout,
  areas: { Header, Main },
} = getEasyLayout(
  LayoutBase,
  styled.div`
    flex: 0 0 auto;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: stretch;
    overflow: hidden;
  `
)`
  header, 1fr
  main, 12fr
  \\ 1fr
`;
const {
  layout: HeaderControls,
  areas: { Title: BaseTitle, Controls, User },
} = getEasyLayout(
  styled(Header)`
    gap: 1em;
  `,
  styled.div`
    flex: 1 0 auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: stretch;
  `
)`
  title controls user, 1fr
  \\ 10fr 2fr 1fr
`;
const Title = styled(BaseTitle)`
  overflow: hidden;
`;
const UserControls = styled(User)`
  flex: 1 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 1em;
`;

const InnerApp: FC = () => {
  const isSigningUpAfterLogout = useMemo<boolean>(() => getIsSigningUpAfterLogout(), []);
  const signUpFlowState = useMemo<SignUpFlowState>(
    () => (isSigningUpAfterLogout ? getStoredSignUpFlowState() : getSignUpFlowStateFromSearch(location.search)),
    [isSigningUpAfterLogout]
  );
  const [oAuthState, setOAuthState] = useState<OAuthState>({});
  const { position_id: positionId, id_code: idCode } = oAuthState;
  const { userData, userManager, signOut, signIn, isLoading: authIsLoading = false } = useAuth();
  const { access_token: authorization = '' } = userData || {};
  const onLogout = useCallback(async () => {
    await signOut();
    window.location.href = SIGNOUT_URL as string;
  }, [signOut]);
  const goToDocumentAcceptance = useCallback(() => {
    if (userData) {
      RELOADED_SINCE_SIGN_UP_ATTEMPT = false;
      storeSignUpFlowStateForLogout(signUpFlowState);
      void onLogout();
    } else {
      history.pushState({}, '', SIGN_UP_DOCUMENTS_PATH);
    }
  }, [userData, onLogout, signUpFlowState]);
  const onCancelAcceptance = useCallback(() => {
    history.pushState({}, '', '/');
  }, []);
  const signUp = useCallback(async () => {
    await SIGNUP_USER_MANAGER.signinRedirect({ state: getSignUpOAuthState(signUpFlowState) });
  }, [signUpFlowState]);
  const userGroups = useMemo<string[]>(() => {
    const { profile = {} } = userData || {};
    const { ['cognito:groups']: groups = [] } = profile as any;
    return groups;
  }, [userData]);
  const adminInfo = useMemo<AdminInfo>(() => getAdminInfo(userGroups), [userGroups]);
  const { isAdmin, isGroupAdmin } = adminInfo;
  const { isResolved: employerAccountStatusIsResolved, isInactiveEmployer } = useEmployerAccountStatus(
    authorization,
    isGroupAdmin
  );
  const { currentWindowPath = '' } = useRouteContext();
  const isEmployerPaymentPath =
    currentWindowPath === EMPLOYER_CHECKOUT || currentWindowPath === EMPLOYER_PAYMENT_COMPLETE;
  const isResolvingEmployerAccount = authorization && !isGroupAdmin && !employerAccountStatusIsResolved;
  const canRenderApplicantFlow =
    authorization && !isGroupAdmin && employerAccountStatusIsResolved && !isInactiveEmployer;
  const visibleHeaderTabData = useMemo(
    () =>
      isEmployerPaymentPath || isInactiveEmployer || isResolvingEmployerAccount
        ? []
        : HEADER_TAB_DATA.filter(({ adminOnly }) => !adminOnly || isAdmin),
    [isAdmin, isEmployerPaymentPath, isInactiveEmployer, isResolvingEmployerAccount]
  );
  const headerTabItems: TabItem[] = useMemo(
    () =>
      visibleHeaderTabData.map(({ label, path }) => ({
        label,
        onClick: () => history.pushState({}, '', path),
      })),
    [visibleHeaderTabData]
  );
  const isLoginPath = currentWindowPath === '/';
  const selectedTabIndex = useMemo<number>(
    () => visibleHeaderTabData.findIndex(({ path }) => currentWindowPath.startsWith(path)),
    [currentWindowPath, visibleHeaderTabData]
  );

  useEffect(() => {
    if (!authIsLoading) {
      if (RELOADED_SINCE_SIGN_UP_ATTEMPT && isSigningUpAfterLogout) {
        clearStoredSignUpFlowState();
        REDIRECTED_TO_SIGN_UP = true;
        history.pushState({}, '', SIGN_UP_DOCUMENTS_PATH);
      } else if (!REDIRECTED_TO_SIGN_UP && !authorization) {
        history.pushState({}, '', '/');
      }
    }
  }, [authIsLoading, isSigningUpAfterLogout, authorization]);

  useEffect(() => {
    if (authorization && isGroupAdmin && getPathIsGroupAdminRestricted(currentWindowPath)) {
      history.pushState({}, '', '/admin');
    }
  }, [authorization, isGroupAdmin, currentWindowPath]);

  useEffect(() => {
    if (
      authorization &&
      employerAccountStatusIsResolved &&
      isInactiveEmployer &&
      !isEmployerPaymentPath
    ) {
      history.pushState({}, '', EMPLOYER_CHECKOUT);
    }
  }, [authorization, employerAccountStatusIsResolved, isInactiveEmployer, isEmployerPaymentPath]);

  return (
    <Layout>
      <HeaderControls>
        <Title>{!isLoginPath ? <LogoImg title="AdvisorInterview by Lead-Advisor" /> : undefined}</Title>
        <Controls>
          {authorization && !isLoginPath && !isGroupAdmin ? (
            <Tabs tabItems={headerTabItems} selectedIndex={selectedTabIndex} />
          ) : undefined}
        </Controls>
        <UserControls>
          {authorization ? (
            <SYM title="Logout" style={{ cursor: 'pointer' }} onClick={onLogout}>
              logout
            </SYM>
          ) : undefined}
        </UserControls>
      </HeaderControls>
      <Main data-id="MAIN">
        <Route exact>
          <Login onSignIn={signIn} goToDocumentAcceptance={goToDocumentAcceptance} />
        </Route>
        <Route path={SIGN_UP_DOCUMENTS_PATH}>
          <Documents onAccept={signUp} onCancel={onCancelAcceptance} />
        </Route>
        <Route path="/login-complete">
          <LoginComplete authorization={authorization} setOAuthState={setOAuthState} />
        </Route>
        <Route path="/profile">
          {canRenderApplicantFlow ? (
            <Profile authorization={authorization} initialPositionId={positionId} initialIdCode={idCode} onLogout={onLogout} />
          ) : undefined}
        </Route>
        <Route path="/interview">
          {canRenderApplicantFlow ? (
            <>
              <Route exact>
                <PurposePreface authorization={authorization} />
              </Route>
              <Route path="/start">
                <Interview authorization={authorization} />
              </Route>
              <Route path="/audio">
                <AudioRecordingScreen authorization={authorization} />
              </Route>
              <Route path="/complete">
                <InterviewComplete />
              </Route>
            </>
          ) : undefined}
        </Route>
        <Route path="/admin">
          {authorization ? <Admin authorization={authorization} adminInfo={adminInfo} /> : undefined}
        </Route>
        <Route path={EMPLOYER_CHECKOUT}>
          {authorization ? <EmployerCheckout authorization={authorization} /> : undefined}
        </Route>
        <Route path={EMPLOYER_PAYMENT_COMPLETE}>
          {authorization ? <EmployerPaymentComplete authorization={authorization} userManager={userManager} /> : undefined}
        </Route>
      </Main>
    </Layout>
  );
};

export const App: FC = () => (
  <ApplicationStateProvider>
    <AuthProvider userManager={USER_MANAGER} autoSignIn={false}>
      <GlobalStyle />
      <Theme />
      <Route>
        <InnerApp />
      </Route>
    </AuthProvider>
  </ApplicationStateProvider>
);
